import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  TextField,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  TextFields as TextFieldsIcon,
  TableChart as TableIcon,
  BarChart as ChartIcon,
  Image as ImageIcon,
  Map as MapIcon,
  AutoAwesome as AutoMapIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useNavigate } from 'react-router-dom';
import {
  DeliverableTemplate,
  TemplateControl,
  TemplateSection,
} from '@horwath-htl/shared-types';

interface TemplateSelectorProps {
  deliverableId: number;
  selectedTemplateId: string | null;
  onTemplateSelect: (templateId: string | null, templateName: string) => void;
}

type TemplateTypeFilter = 'all' | 'global' | 'personal' | 'shared';

/**
 * TemplateSelector - Step 1 of the DeliverableBuilder workflow
 * Allows users to select a template for their deliverable document
 */
export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  deliverableId,
  selectedTemplateId,
  onTemplateSelect,
}) => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  // Filter state
  const [typeFilter, setTypeFilter] = useState<TemplateTypeFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch templates
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['templates', 'selector', typeFilter],
    queryFn: async () => {
      // Build URL with type filter if not 'all'
      const params = new URLSearchParams();
      if (typeFilter !== 'all') {
        params.set('type', typeFilter);
      }
      const queryString = params.toString();
      const url = `/api/deliverables/templates${queryString ? `?${queryString}` : ''}`;

      const response = await authenticatedFetch(url);
      if (!response?.ok) {
        throw new Error('Failed to fetch templates');
      }
      return await response.json();
    },
  });

  const templates: DeliverableTemplate[] = data?.templates || [];

  // Get the selected template details
  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find(t => t.id === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);

  // Filter templates by search term (client-side)
  const filteredTemplates = useMemo(() => {
    if (!searchTerm) return templates;
    const term = searchTerm.toLowerCase();
    return templates.filter(
      (template) =>
        template.name.toLowerCase().includes(term) ||
        (template.description?.toLowerCase().includes(term) ?? false)
    );
  }, [templates, searchTerm]);

  // Handle template click
  const handleTemplateClick = (template: DeliverableTemplate) => {
    if (selectedTemplateId === template.id) {
      // Deselect if already selected
      onTemplateSelect(null, '');
    } else {
      onTemplateSelect(template.id, template.name);
    }
  };

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: TemplateTypeFilter) => {
    setTypeFilter(newValue);
  };

  // Handle template download
  const handleDownload = async (template: DeliverableTemplate, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent template selection
    try {
      const response = await authenticatedFetch(
        `/api/deliverables/templates/${template.id}/download`
      );
      if (response?.ok) {
        const data = await response.json();
        if (data.success && data.downloadUrl) {
          window.open(data.downloadUrl, '_blank');
        }
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Get type badge color
  const getTypeColor = (type: string): 'primary' | 'secondary' | 'info' => {
    switch (type) {
      case 'global':
        return 'primary';
      case 'personal':
        return 'secondary';
      case 'shared':
        return 'info';
      default:
        return 'primary';
    }
  };

  // Get type icon
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'global':
        return <PublicIcon fontSize="small" />;
      case 'personal':
        return <PersonIcon fontSize="small" />;
      case 'shared':
        return <ShareIcon fontSize="small" />;
      default:
        return <DescriptionIcon fontSize="small" />;
    }
  };

  // Get control type icon
  const getControlTypeIcon = (type: string) => {
    switch (type) {
      case 'rich_text':
        return <TextFieldsIcon fontSize="small" />;
      case 'table':
        return <TableIcon fontSize="small" />;
      case 'chart':
        return <ChartIcon fontSize="small" />;
      case 'image':
        return <ImageIcon fontSize="small" />;
      case 'map':
        return <MapIcon fontSize="small" />;
      case 'auto_mapped':
        return <AutoMapIcon fontSize="small" />;
      default:
        return <TextFieldsIcon fontSize="small" />;
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Count controls
  const getControlCount = (template: DeliverableTemplate): number => {
    return template.controls_config?.controls?.length ?? 0;
  };

  // Truncate description
  const truncateDescription = (description: string | null, maxLength: number = 100) => {
    if (!description) return 'No description';
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength) + '...';
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, height: '100%' }}>
      {/* Left Panel - Template List */}
      <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column' }}>
        {/* Tabs for type filtering */}
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
            <Tabs
              value={typeFilter}
              onChange={handleTabChange}
              aria-label="template type filter"
            >
              <Tab label="All" value="all" />
              <Tab label="Global" value="global" />
              <Tab label="Personal" value="personal" />
              <Tab label="Shared with Me" value="shared" />
            </Tabs>
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Search Input */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search templates by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Loading State */}
        {isLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: 300 }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            {error instanceof Error ? error.message : 'Failed to load templates'}
          </Alert>
        )}

        {/* Empty State */}
        {!isLoading && !isError && filteredTemplates.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No templates found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchTerm
                ? 'Try adjusting your search terms or clearing filters.'
                : typeFilter !== 'all'
                ? `No ${typeFilter} templates available.`
                : 'No templates are available yet.'}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/deliverables/templates')}
            >
              Manage Templates
            </Button>
          </Paper>
        )}

        {/* Template Grid */}
        {!isLoading && !isError && filteredTemplates.length > 0 && (
          <Box sx={{ overflowY: 'auto', flex: 1 }}>
            <Grid container spacing={2}>
              {filteredTemplates.map((template) => (
                <Grid item xs={12} sm={6} key={template.id}>
                  <Card
                    sx={{
                      height: '100%',
                      border: selectedTemplateId === template.id ? 2 : 1,
                      borderColor:
                        selectedTemplateId === template.id
                          ? 'primary.main'
                          : 'divider',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        borderColor:
                          selectedTemplateId === template.id
                            ? 'primary.main'
                            : 'primary.light',
                        boxShadow: 2,
                      },
                      position: 'relative',
                    }}
                  >
                    {/* Selection indicator */}
                    {selectedTemplateId === template.id && (
                      <CheckCircleIcon
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                        }}
                      />
                    )}
                    <CardActionArea
                      onClick={() => handleTemplateClick(template)}
                      sx={{ height: '100%' }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                          <Typography variant="subtitle1" fontWeight="medium" sx={{ pr: 3 }}>
                            {template.name}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, minHeight: 40 }}
                        >
                          {truncateDescription(template.description)}
                        </Typography>

                        <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                          <Chip
                            icon={getTypeIcon(template.type)}
                            label={template.type}
                            color={getTypeColor(template.type)}
                            size="small"
                          />
                          {getControlCount(template) > 0 && (
                            <Chip
                              label={`${getControlCount(template)} controls`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                          Created {formatDate(template.created_at)}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>

      {/* Right Panel - Template Preview */}
      <Paper
        sx={{
          flex: 1,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 300,
          maxWidth: 400,
        }}
        elevation={2}
      >
        {!selectedTemplate ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            color="text.secondary"
          >
            <DescriptionIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" textAlign="center">
              Select a template to see its details
            </Typography>
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6">{selectedTemplate.name}</Typography>
              <Tooltip title="Download Template">
                <IconButton
                  size="small"
                  onClick={(e) => handleDownload(selectedTemplate, e)}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Box display="flex" gap={1} mb={2}>
              <Chip
                icon={getTypeIcon(selectedTemplate.type)}
                label={selectedTemplate.type}
                color={getTypeColor(selectedTemplate.type)}
                size="small"
              />
              <Chip
                label={selectedTemplate.is_active ? 'Active' : 'Inactive'}
                color={selectedTemplate.is_active ? 'success' : 'default'}
                size="small"
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Description */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Description
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedTemplate.description || 'No description provided'}
            </Typography>

            {/* Language */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Language
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedTemplate.language_code.toUpperCase()}
            </Typography>

            {/* Version */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Version
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedTemplate.version}
            </Typography>

            <Divider sx={{ my: 1 }} />

            {/* Controls */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Template Controls ({getControlCount(selectedTemplate)})
            </Typography>
            {selectedTemplate.controls_config?.controls &&
            selectedTemplate.controls_config.controls.length > 0 ? (
              <List dense sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {selectedTemplate.controls_config.controls.map((control: TemplateControl) => (
                  <ListItem key={control.id} disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {getControlTypeIcon(control.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={control.name}
                      secondary={control.type.replace('_', ' ')}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    {control.required && (
                      <Chip label="Required" size="small" color="warning" variant="outlined" />
                    )}
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No controls detected
              </Typography>
            )}

            {/* Sections */}
            {selectedTemplate.sections_config?.sections &&
              selectedTemplate.sections_config.sections.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Sections ({selectedTemplate.sections_config.sections.length})
                  </Typography>
                  <List dense sx={{ maxHeight: 150, overflowY: 'auto' }}>
                    {selectedTemplate.sections_config.sections
                      .sort((a: TemplateSection, b: TemplateSection) => a.order - b.order)
                      .map((section: TemplateSection) => (
                        <ListItem key={section.id} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={section.name}
                            secondary={section.description}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                          {section.required && (
                            <Chip
                              label="Required"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </ListItem>
                      ))}
                  </List>
                </>
              )}

            <Divider sx={{ my: 1 }} />

            {/* Metadata */}
            <Typography variant="caption" color="text.secondary" display="block">
              Created: {formatDate(selectedTemplate.created_at)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Updated: {formatDate(selectedTemplate.updated_at)}
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default TemplateSelector;
